import {
  Close,
  FileDownloadOutlined,
  Search,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  isPreviewableFile,
  truncateFilename,
} from '../../../helpers/knowledgeHelper';
import {
  KNOWLEDGE_FILE_SEARCH_MIN_LENGTH,
  useKnowledgeFileSearch,
} from '../../../hooks/queries/useKnowledgeFileSearch';
import { useTranslation } from '../../../hooks/useTranslation';
import { KnowledgeFileSearchResult } from '../../../interfaces/Knowledge';
import Button from '../../shared/Button/Button';
import Text from '../../shared/Text/Text';
import KnowledgeFilePreviewModal from '../KnowledgeFilePreviewModal/KnowledgeFilePreviewModal';
import StyledFileName from '../Table/StyledFileName';
import StyledKnowledgeItems from '../Table/StyledKnowledgeItems';
import actionIconButtonStyle from './actionIconButtonStyle';
import HighlightedSnippet from './HighlightedSnippet';

// Each change is a backend call, so wait for the user to stop typing
const SEARCH_DEBOUNCE_MS = 350;

interface KnowledgeFileSearchBarProps {
  knowledgeId: string;
  onDownload: (fileName: string) => void;
  // True while results replace the file list, so the parent can hide it
  onActiveChange?: (active: boolean) => void;
}

const SearchResultItem = ({
  result,
  query,
  onDownload,
  onPreview,
}: {
  result: KnowledgeFileSearchResult;
  query: string;
  onDownload: (fileName: string) => void;
  onPreview: (result: KnowledgeFileSearchResult) => void;
}) => {
  const { t } = useTranslation();
  const [showAllSnippets, setShowAllSnippets] = useState(false);
  const [firstSnippet, ...otherSnippets] = result.snippets;

  return (
    <li>
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Tooltip title={result.file_name} arrow placement="top">
          <StyledFileName style={{ display: 'block' }}>
            {truncateFilename(result.file_name)}
          </StyledFileName>
        </Tooltip>
        {firstSnippet && (
          <HighlightedSnippet snippet={firstSnippet} query={query} />
        )}
        {showAllSnippets &&
          otherSnippets.map((snippet, index) => (
            <HighlightedSnippet key={index} snippet={snippet} query={query} />
          ))}
        {otherSnippets.length > 0 && (
          <MuiButton
            type="button"
            size="small"
            sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
            onClick={() => setShowAllSnippets(shown => !shown)}
          >
            {showAllSnippets ? t('Show fewer matches') : t('Show more matches')}
          </MuiButton>
        )}
      </Box>
      <Stack direction="row" gap="1rem" alignItems="center">
        <Chip
          size="small"
          label={
            result.match_count === 1
              ? t('1 match')
              : t('{{count}} matches', { count: result.match_count })
          }
        />
        {isPreviewableFile(result.file_name) && (
          <Tooltip title={t('Preview file')} arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                padding="0.5rem"
                style={actionIconButtonStyle}
                aria-label={t('Preview {{file}}', { file: result.file_name })}
                onClick={() => onPreview(result)}
              >
                <VisibilityOutlined />
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={t('Download file')} arrow>
          <span>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              padding="0.5rem"
              style={actionIconButtonStyle}
              aria-label={t('Download {{file}}', { file: result.file_name })}
              onClick={() => onDownload(result.file_name)}
            >
              <FileDownloadOutlined />
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </li>
  );
};

const KnowledgeFileSearchBar = ({
  knowledgeId,
  onDownload,
  onActiveChange,
}: KnowledgeFileSearchBarProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [previewResult, setPreviewResult] =
    useState<KnowledgeFileSearchResult | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => setQuery(input), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [input]);

  const trimmedQuery = query.trim();
  const isActive = trimmedQuery.length >= KNOWLEDGE_FILE_SEARCH_MIN_LENGTH;
  const {
    data: results,
    isFetching,
    isError,
  } = useKnowledgeFileSearch(knowledgeId, query);

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive]);

  const clearSearch = () => {
    setInput('');
    setQuery('');
  };

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        value={input}
        placeholder={t(
          'Search within the uploaded files for a sentence, link or name'
        )}
        onChange={event => setInput(event.target.value)}
        onKeyDown={event => {
          // The bar sits inside the knowledge form: Enter must not save it
          if (event.key === 'Enter') event.preventDefault();
        }}
        inputProps={{
          'aria-label': t(
            'Search within the uploaded files for a sentence, link or name'
          ),
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isFetching && <CircularProgress size={16} />}
              {input && (
                <IconButton
                  size="small"
                  aria-label={t('Clear search')}
                  onClick={clearSearch}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {isActive && isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('Search failed. Please try again.')}
        </Alert>
      )}

      {isActive && results && results.length === 0 && (
        <Text type="body2" color="#666" sx={{ mt: 2 }}>
          {t('No documents contain "{{query}}"', { query: trimmedQuery })}
        </Text>
      )}

      {isActive && results && results.length > 0 && (
        <>
          <Text type="h4" sx={{ mt: 2 }}>
            {t('Files containing "{{query}}" ({{count}})', {
              query: trimmedQuery,
              count: results.length,
            })}
          </Text>
          <StyledKnowledgeItems>
            {results.map(result => (
              <SearchResultItem
                key={result.file_name}
                result={result}
                query={trimmedQuery}
                onDownload={onDownload}
                onPreview={setPreviewResult}
              />
            ))}
          </StyledKnowledgeItems>
        </>
      )}

      <KnowledgeFilePreviewModal
        key={previewResult?.file_name}
        knowledgeId={knowledgeId}
        result={previewResult}
        query={trimmedQuery}
        onClose={() => setPreviewResult(null)}
        onDownload={onDownload}
      />
    </Box>
  );
};

export default KnowledgeFileSearchBar;
